import { useUpdateApPayeeAiServiceEnablements } from '@/context/services/ai_service_setting/AiServiceSetting.service';
import { usePayeeDetailDrawer } from '@/features/ap_payees/components/PayeeDetailDrawer/context/PayeeDetailDrawerProvider';
import { useScrollToSectionOnEdit } from '@/features/ap_payees/components/PayeeDetailDrawer/hooks/useScrollToSectionOnEdit';
import type { DataMaster } from '@/features/ap_payees/components/PayeeDetailDrawer/types';
import {
  AnchorItemKey,
  SECTION_KEYS,
} from '@/features/ap_payees/components/PayeeDetailDrawer/types';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, useCallback } from 'react';
import styles from '../../styles.module.scss';
import { Edit, FormValues } from './Edit';
import { View } from './View';

const cx = classnames.bind(styles);

type Props = {
  apAiServiceSettings: DataMaster['apAiServiceSettings'];
  apPayeeId: string;
  isEditable?: boolean;
};

export const ApAiServiceSettings: FC<Props> = ({
  apAiServiceSettings,
  apPayeeId,
  isEditable = false,
}) => {
  const { t } = useTranslation();
  const notify = useToastAPI();
  const {
    isEditingSection,
    setIsEditingSection,
    onEditFormDirtyChange,
    handleCancelEdit,
    refetch,
  } = usePayeeDetailDrawer();
  const { mutateAsync, isPending } = useUpdateApPayeeAiServiceEnablements();
  const isEditing = isEditingSection === SECTION_KEYS.AP_AI_SERVICE;
  const sectionRef = useScrollToSectionOnEdit(
    isEditing,
    SECTION_KEYS.AP_AI_SERVICE
  );

  const handleEdit = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setIsEditingSection(SECTION_KEYS.AP_AI_SERVICE);
    },
    [setIsEditingSection]
  );

  const handleCancel = useCallback(
    (isDirty: boolean) => {
      handleCancelEdit?.(isDirty);
    },
    [handleCancelEdit]
  );

  const handleDirtyChange = useCallback(
    (isDirty: boolean) => {
      onEditFormDirtyChange?.(isDirty);
    },
    [onEditFormDirtyChange]
  );

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        const result = await mutateAsync(
          apPayeeId,
          Object.fromEntries(
            apAiServiceSettings.map((service) => [
              service.service_type,
              values[service.service_type] ?? false,
            ])
          )
        );

        if (result.isSuccessful) {
          await refetch();
          setIsEditingSection(SECTION_KEYS.NULL);
          notify.success({
            title: t('ai_service_setting.service_update_success'),
            duration: 5,
          });
          return;
        }

        notify.error({
          title: result.message,
          duration: 5,
        });
      } catch (error) {
        notify.error({
          title: error instanceof Error ? error.message : t('system_error'),
          duration: 5,
        });
      }
    },
    [
      apAiServiceSettings,
      apPayeeId,
      mutateAsync,
      notify,
      refetch,
      setIsEditingSection,
      t,
    ]
  );

  return (
    <div
      ref={sectionRef}
      id={AnchorItemKey.AP_AI_SERVICE_SETTING}
      className={cx(styles['sectionContainer'])}
    >
      {isEditing ? (
        <Edit
          services={apAiServiceSettings}
          isPending={isPending}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          onDirtyChange={handleDirtyChange}
        />
      ) : (
        <View
          services={apAiServiceSettings}
          isEditable={isEditable}
          isEditDisabled={isEditingSection !== SECTION_KEYS.NULL}
          onEdit={handleEdit}
        />
      )}
    </div>
  );
};

ApAiServiceSettings.displayName = 'ApAiServiceSettings';
